import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { X as XIcon } from "lucide-react";
import { capture } from "../../analytics/posthog";
import { useUser } from "../login/UserContext";
import { checklistIsOld } from "../../features/first-session/checklist";
import { loadProgress } from "../../features/lifecycle/progress-store";
import {
  Announcement,
  dismiss,
  getActiveAnnouncement,
  markShown,
  waitsForChecklist,
} from "./announcements";
import styles from "./announcements.module.scss";

/**
 * App-root host for in-app launch announcements. Mounts once (next to
 * {@link ../feed/PostToFeedToast}) and renders at most one compact card —
 * above the bottom bar on mobile, bottom-right on desktop. Which card (if
 * any) is driven by the registry in ./announcements.ts, and only signed-in
 * members ever see one. The first display is persisted so a reload or
 * navigation never repeats it, even when the member didn't click the CTA or X.
 */
const AnnouncementHost: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, authReady } = useUser();
  const isMember = authReady && Boolean(user?.id);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    // Navigation ends this display; it must not follow the visitor around.
    setAnnouncement(null);
    // Signed-out visits must not spend the seen flag.
    if (!isMember) return;
    let live = true;
    // Small delay so the card never races first paint of the page itself
    // (same courtesy TermsGate extends, minus the blocking backdrop).
    const timer = setTimeout(async () => {
      const active = getActiveAnnouncement(pathname);
      if (!active) return;
      // The first closing line result lands here, so the card waits until that
      // visit is well behind them. An unknown answer waits too, and leaves the
      // seen flag unspent for a later visit.
      if (waitsForChecklist(pathname)) {
        const progress = await loadProgress();
        if (!progress || !checklistIsOld(progress)) return;
      }
      if (!live || !markShown(active.id)) return;
      capture("announcement_shown", { id: active.id });
      setAnnouncement(active);
    }, 900);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [pathname, isMember]);

  if (!announcement) return null;

  const close = (reason: "cta" | "dismissed") => {
    dismiss(announcement.id);
    capture(`announcement_${reason === "cta" ? "clicked" : "dismissed"}`, {
      id: announcement.id,
    });
    setAnnouncement(null);
    if (reason === "cta") navigate(announcement.ctaTo);
  };

  return createPortal(
    <div
      className={styles.announcementCard}
      role="status"
      aria-label={announcement.title}
    >
      <button
        type="button"
        className={styles.announcementClose}
        aria-label="Dismiss announcement"
        onClick={() => close("dismissed")}
      >
        <XIcon size={15} />
      </button>
      <span className={styles.announcementEyebrow}>
        <span className={styles.announcementDot} />
        {announcement.eyebrow}
      </span>
      <h4 className={styles.announcementTitle}>{announcement.title}</h4>
      <p className={styles.announcementBody}>{announcement.body}</p>
      <button
        type="button"
        className={styles.announcementCta}
        onClick={() => close("cta")}
      >
        {announcement.ctaLabel}
      </button>
    </div>,
    document.body
  );
};

export default AnnouncementHost;
